import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "pt-BR" | "en-US";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Simple dictionary for AppShell (Sidebar & Header)
const translations: Record<Language, Record<string, string>> = {
  "pt-BR": {
    "Data Ingestion": "Ingestão de Dados",
    "Upload Engine": "Motor de Upload",
    "Strategy AI": "IA Estratégica",
    "Executive Vision": "Visão Executiva",
    "Offline Core": "Núcleo Offline",
    "Market Analytics": "Análise de Mercado",
    "Science Hub": "Polo Científico",
    "Exploration": "Exploração",
    "XML Datasets": "Bases de XML",
    "Product Trends": "Tendências de Produtos",
    "Tax Intelligence": "Inteligência Fiscal",
    "NCM Classifier": "Classificador NCM",
    "Governance": "Governança",
    "Compliance Flow": "Fluxo de Compliance",
    "Structural Audit": "Validação Estrutural",
    "Exports": "Exportações",
    "Dynamic Reports": "Relatórios Dinâmicos",
    "Batch Export": "Exportação em Lote",
    "System Admin": "Admin do Sistema",
    "Management": "Gestão",
    "System Online": "Sistema Online",
    "Support Center": "Central de Ajuda",
    "Sign Out": "Sair",
    "Return": "Voltar",
    "Search Entity by CNPJ...": "Buscar Entidade por CNPJ...",
    "Sync": "Sincronizar",
  },
  "en-US": {
    "Data Ingestion": "Data Ingestion",
    "Upload Engine": "Upload Engine",
    "Strategy AI": "Strategy AI",
    "Executive Vision": "Executive Vision",
    "Offline Core": "Offline Core",
    "Market Analytics": "Market Analytics",
    "Science Hub": "Science Hub",
    "Exploration": "Exploration",
    "XML Datasets": "XML Datasets",
    "Product Trends": "Product Trends",
    "Tax Intelligence": "Tax Intelligence",
    "NCM Classifier": "NCM Classifier",
    "Governance": "Governance",
    "Compliance Flow": "Compliance Flow",
    "Structural Audit": "Structural Audit",
    "Exports": "Exports",
    "Dynamic Reports": "Dynamic Reports",
    "Batch Export": "Batch Export",
    "System Admin": "System Admin",
    "Management": "Management",
    "System Online": "System Online",
    "Support Center": "Support Center",
    "Sign Out": "Sign Out",
    "Return": "Return",
    "Search Entity by CNPJ...": "Search Entity by CNPJ...",
    "Sync": "Sync",
  }
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("app_language");
    return (saved === "pt-BR" || saved === "en-US") ? saved : "pt-BR";
  });

  useEffect(() => {
    localStorage.setItem("app_language", language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "pt-BR" ? "en-US" : "pt-BR"));
  };

  const t = (key: string) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
